import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import schema from './disciplinaTab.schema';
import DisciplinaItem from './DisciplinaItem';
import Button from 'Components/UI/Button';
import { IconName } from 'Components/UI/Icon';
import Form from 'Components/UI/Form/Form';
import Field from 'Components/UI/Field';
import { IDiscipline, IDisciplineData } from 'Data/interfaces/Obra/IObraData';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { disciplineIcons } from 'Utils/Icons';
import { Container } from './styles';

interface IDiscipliansTab {
  isSubmited?: boolean;
  onSubmit: (props?: object, error?: string[]) => void;
}

const DiscipliansTab: FC<Props> = ({
  isSubmited,
  disciplines,
  disciplinesData,
  theme,
  onSubmit,
}) => {
  const { t } = useTranslation('obras');
  const [disciplinesList, setDisciplinesList] = useState<IDisciplineData[]>([]);

  const onCheck = (index: number) => {
    disciplinesList[index].IsChecked = !disciplinesList[index].IsChecked;
    setDisciplinesList(JSON.parse(JSON.stringify(disciplinesList)));
  };

  const addDiscipline = (data: any) => {
    const newDiscipline: IDisciplineData = {
      DisciplineId: 9,
      Name: t('cadastroObra.disciplinasTab.customName'),
      CustomDispName: data.CustomDispName,
      IsChecked: true,
      IsCustom: true,
      Icon: 'menuContexto',
    };
    setDisciplinesList([...disciplinesList, newDiscipline]);
  };

  const hasErrors = () => {
    const withoutDisciplines = disciplinesList.find((discipline) => discipline.IsChecked) === undefined;
    if (withoutDisciplines) {
      return [t('cadastroObra.disciplinasTab.atLeastOneRequired')];
    }
  };

  useEffect(() => {
    const list: IDisciplineData[] = JSON.parse(JSON.stringify(disciplinesData));

    disciplines?.forEach((discipline) => {
      const disciplineData = list?.find((disciplineChecked) =>
        !disciplineChecked.IsCustom && disciplineChecked.DisciplineId === discipline.DisciplineFk);

      if (disciplineData) {
        disciplineData.IsChecked = true;
        disciplineData.Disabled = true;
      } else {
        list.push({
          Name: t('cadastroObra.disciplinasTab.customName'),
          CustomDispName: discipline.CustomName,
          DisciplineId: 9,
          Icon: 'menuContexto',
          IsChecked: true,
          IsCustom: true,
          Disabled: true
        });
      }
    });

    setDisciplinesList([...list]);
  }, [disciplines, disciplinesData]);

  if (isSubmited) {
    const errors = hasErrors();
    if (errors !== undefined) {
      onSubmit(undefined, errors);
    } else {
      const Disciplines: IDiscipline[] = disciplinesList
        .filter((discipline) => discipline.IsChecked)
        .map((discipline) => ({
          CustomName: discipline.CustomDispName,
          DisciplineFk: discipline.DisciplineId,
          IsCustom: discipline.IsCustom,
        }));

      onSubmit({ Disciplines: [...Disciplines] });
    }
  }

  return (
    <Container>
      <div>
        <span className="titleTab">
          {t('cadastroObra.disciplinasTab.title')}
        </span>

        <div className="disciplinasList">
          {disciplinesList.map((disciplina, index) => (
            <DisciplinaItem
              key={index}
              disciplina={disciplina.CustomDispName ?? disciplina.Name}
              icon={disciplineIcons.get(disciplina.Icon) as IconName}
              checked={disciplina.IsChecked}
              disabled={disciplina.Disabled}
              onCheck={() => onCheck(index)}
              theme={theme}
            />
          ))}
        </div>
      </div>

      <div className="novaDisciplinaContainer">
        <span className="titleTab">
          {t('cadastroObra.disciplinasTab.noDisciplineFound')}
        </span>
        <Form onSubmit={addDiscipline} schema={schema} resetForm={true}>
          <div className="formNovaDisciplina">
            <Field name="CustomDispName" label={t('cadastroObra.disciplinasTab.newDisciplineLabel')} />
            <Button type="primary" htmlType="submit" height={34}>
              <span className="titleButton">{t('cadastroObra.disciplinasTab.create')}</span>
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  disciplines: state.obra.Obra.Disciplines,
  disciplinesData: state.obra.Disciplines,
  theme: state.multiDomain.theme,
});

const connector = connect(
  mapState,
);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IDiscipliansTab;

export default connector(DiscipliansTab);
